import { useEffect, useState } from 'react';
import { SelectionGroup, Modal, ModalYesNo, Select, WEEKDAYS } from '@scripsteam/scrips-signal-ds';
import { Button } from '@scripsteam/scrips-signal-ds';
import { TextField } from '@scripsteam/scrips-signal-ds';
import { Switch } from '@scripsteam/scrips-signal-ds';
import { FieldLabel } from '@scripsteam/scrips-signal-ds';
import { useSaveAppointmentTemplate } from '@/settings/services';
import type {
  AppointmentProfileEntry,
  AppointmentTemplateEntry,
  AppointmentTemplateEnvelope,
  AppointmentTemplateTimeData,
} from '@/types/settings-api';

/**
 * Appointment Template editor — PROD-762.
 *
 * Flutter parity: `edit_appointment_template_widget.dart`. Edits a
 * single template entry; submits the FULL practitioner template list
 * to `POST /api/Doctor/AppointmentTemplate` with `isUpdated: true` on
 * the edited row and `false` on others. Deletions go into the
 * `isDeleted: string[]` array as the deleted row's GUID and are also
 * removed from `request[]`.
 *
 * Same list-snapshot semantics as Calendar Settings save — backend
 * reads `isUpdated` to decide which rows to write through.
 *
 * Add-mode (initial=null) synthesises a blank template with empty
 * profileId / practiceId / weekDay (user must pick before Save
 * validates). Defaults Mon 9 AM → 5 PM as a reasonable starting
 * shape (Flutter pre-fills the same defaults).
 */

// SelectionGroup options: value is the full day name (wire shape:
// `weekDay: string[]`), label is the 3-char abbreviation shown on the chip.
const WEEKDAY_OPTIONS = WEEKDAYS.map((day) => ({
  value: day,
  label: day.slice(0, 3),
}));

const HOURS_1_TO_12 = Array.from({ length: 12 }, (_, i) => i + 1);
const MINUTES_STEP_5 = Array.from({ length: 12 }, (_, i) => i * 5);

export interface PracticeOption {
  practiceSetupId: string | null;
  practiceName: string | null;
}

export interface AppointmentTemplateEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Existing entry to edit, or null for Add. */
  initial: AppointmentTemplateEntry | null;
  /** Current full practitioner template list (used to build the save envelope). */
  allTemplates: AppointmentTemplateEntry[];
  /** Practitioner's appointment profile list — feeds the profile picker. */
  profiles: AppointmentProfileEntry[];
  /** Practice list — feeds the practice picker on Add. */
  practiceOptions: PracticeOption[];
  doctorSetupId: string;
  organizationId: string;
}

function makeBlankTemplate(
  doctorSetupId: string,
  organizationId: string,
): AppointmentTemplateEntry {
  return {
    id: '', // empty on Add — backend allocates
    organizationId,
    practiceId: '',
    practitionerId: doctorSetupId, // CORRECT spelling on body
    templateName: '',
    appointmentProfileId: '',
    weekDay: [],
    startTime: { hour: 9, minute: 0, format: 'AM' },
    endTime: { hour: 5, minute: 0, format: 'PM' },
    isUpdated: true,
    isActive: true,
  };
}

export function AppointmentTemplateEditDialog({
  open,
  onOpenChange,
  initial,
  allTemplates,
  profiles,
  practiceOptions,
  doctorSetupId,
  organizationId,
}: AppointmentTemplateEditDialogProps) {
  const isAdd = initial === null;
  const [draft, setDraft] = useState<AppointmentTemplateEntry>(
    initial ?? makeBlankTemplate(doctorSetupId, organizationId),
  );
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setDraft(
        initial ?? makeBlankTemplate(doctorSetupId, organizationId),
      );
    }
  }, [open, initial, doctorSetupId, organizationId]);

  const saveMutation = useSaveAppointmentTemplate(doctorSetupId, organizationId);

  // Profile picker is scoped to the selected practice (Flutter parity:
  // profile selector filters by practiceId after a practice is picked).
  const profilesForPractice = profiles.filter(
    (p) => !draft.practiceId || p.practiceId === draft.practiceId,
  );

  const handleSave = () => {
    // Build the FULL list with isUpdated flags. Add: draft is appended
    // with isUpdated:true. Edit: replace the matching row, set
    // isUpdated:true; others stay at isUpdated:false.
    const next = isAdd
      ? [
          ...allTemplates.map((t) => ({ ...t, isUpdated: false })),
          { ...draft, isUpdated: true },
        ]
      : allTemplates.map((t) =>
          t.id === draft.id
            ? { ...draft, isUpdated: true }
            : { ...t, isUpdated: false },
        );
    const envelope: AppointmentTemplateEnvelope = {
      request: next,
      isDeleted: [],
    };
    saveMutation.mutate(envelope, {
      onSuccess: () => onOpenChange(false),
    });
  };

  const handleDelete = () => {
    if (!draft.id) return;
    // Soft-archive: remove the row from `request` and add its GUID
    // to `isDeleted`. Mirrors Flutter EditAppointmentTemplateWidget
    // delete-row dispatch.
    const remaining = allTemplates.filter((t) => t.id !== draft.id);
    const envelope: AppointmentTemplateEnvelope = {
      request: remaining.map((t) => ({ ...t, isUpdated: false })),
      isDeleted: [draft.id],
    };
    saveMutation.mutate(envelope, {
      onSuccess: () => {
        setConfirmDeleteOpen(false);
        onOpenChange(false);
      },
    });
  };

  const validPractices = practiceOptions.filter(
    (p): p is { practiceSetupId: string; practiceName: string | null } =>
      !!p.practiceSetupId,
  );

  const setField = <K extends keyof AppointmentTemplateEntry>(
    key: K,
    value: AppointmentTemplateEntry[K],
  ) => setDraft({ ...draft, [key]: value });

  const setTimeField = (
    target: 'startTime' | 'endTime',
    patch: Partial<AppointmentTemplateTimeData>,
  ) => setDraft({ ...draft, [target]: { ...draft[target], ...patch } });

  const canSave =
    !saveMutation.isPending &&
    !!draft.templateName.trim() &&
    !!draft.practiceId &&
    !!draft.appointmentProfileId &&
    draft.weekDay.length > 0;

  return (
    <>
      <Modal
      open={open}
      onClose={() => onOpenChange(false)}
      title={isAdd ? 'Add Appointment Template' : 'Edit Appointment Template'}
      size="lg"
      footer={<><div className="flex items-center gap-2">
              <Button type="button" disabled={!canSave} onClick={handleSave}>
                {saveMutation.isPending ? 'Saving…' : 'Save'}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => onOpenChange(false)}
                disabled={saveMutation.isPending}
              >
                Cancel
              </Button>
            </div>
            {!isAdd && (
              <Button
                type="button"
                variant="tertiary"
                disabled={saveMutation.isPending}
                className="text-status-error"
                onClick={() => setConfirmDeleteOpen(true)}
              >
                Delete
              </Button>
            )}</>}
      footerAlign="between"
    >
          

          <div className="space-y-4 py-2 max-h-overlay-body overflow-y-auto scrollbar-hide">
            <div>
              <FieldLabel stacked>
                Template name <span className="text-status-error">*</span>
              </FieldLabel>
              <TextField
                value={draft.templateName}
                placeholder="e.g. Vaccinations, Walk-ins"
                maxLength={120}
                onChange={(e) => setField('templateName', e.target.value)}
              />
            </div>

            <div>
              <FieldLabel stacked>
                Practice <span className="text-status-error">*</span>
              </FieldLabel>
              {validPractices.length === 0 ? (
                <p className="text-sm text-text-muted">
                  No practices assigned to this practitioner.
                </p>
              ) : (
                <Select
                  value={draft.practiceId || ''}
                  onChange={(v) => {
                    // Changing practice invalidates the selected profile
                    // (profile is practice-scoped). Same Flutter behavior.
                    setDraft({
                      ...draft,
                      practiceId: v ?? '',
                      appointmentProfileId: '',
                    });
                  }}
                  placeholder="Select practice"
                  options={validPractices.map((p) => ({
                    label: p.practiceName ?? '—',
                    value: p.practiceSetupId,
                  }))}
                />
              )}
            </div>

            <div>
              <FieldLabel stacked>
                Appointment profile <span className="text-status-error">*</span>
              </FieldLabel>
              {!draft.practiceId ? (
                <p className="text-sm text-text-muted">
                  Pick a practice first.
                </p>
              ) : profilesForPractice.length === 0 ? (
                <p className="text-sm text-text-muted">
                  This practice has no appointment profiles yet.
                </p>
              ) : (
                <Select
                  value={draft.appointmentProfileId || ''}
                  onChange={(v) =>
                    setField('appointmentProfileId', v ?? '')
                  }
                  placeholder="Select profile"
                  options={profilesForPractice.map((p) => ({
                    label: p.profileName ?? '—',
                    value: p.id,
                  }))}
                />
              )}
            </div>

            <div>
              <FieldLabel stacked>
                Weekdays <span className="text-status-error">*</span>
              </FieldLabel>
              <div className="mt-2">
                <SelectionGroup
                  aria-label="Weekdays"
                  options={WEEKDAY_OPTIONS}
                  value={draft.weekDay}
                  onChange={(next) => setField('weekDay', next)}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <TimeField
                label="Start time"
                value={draft.startTime}
                onChange={(patch) => setTimeField('startTime', patch)}
              />
              <TimeField
                label="End time"
                value={draft.endTime}
                onChange={(patch) => setTimeField('endTime', patch)}
              />
            </div>

            {!isAdd && (
              <div className="flex items-center justify-between border-t border-border-hairline pt-3">
                <span className="text-sm text-text-default">Active</span>
                <Switch
                  checked={draft.isActive}
                  onCheckedChange={(v) => setField('isActive', v)}
                  aria-label="Active"
                />
              </div>
            )}
          </div>

          
        </Modal>

      <ModalYesNo
      open={confirmDeleteOpen}
      onCancel={() => setConfirmDeleteOpen(false)}
      title="Delete this appointment template?"
      message="The template will be archived and removed from this
              practitioner's schedule. Existing appointments booked from
              it are unaffected."
      cancelLabel="Cancel"
      confirmLabel={saveMutation.isPending ? 'Deleting…' : 'Delete'}
      onConfirm={handleDelete}
      loading={saveMutation.isPending}
    />
    </>
  );
}

function TimeField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: AppointmentTemplateTimeData;
  onChange: (patch: Partial<AppointmentTemplateTimeData>) => void;
}) {
  return (
    <div>
      <FieldLabel stacked>{label}</FieldLabel>
      <div className="grid grid-cols-3 gap-2">
        <Select
          aria-label={`${label} hour`}
          size="compact"
          value={value.hour.toString()}
          onChange={(v) => v && onChange({ hour: Number(v) })}
          options={HOURS_1_TO_12.map((h) => ({
            label: h.toString().padStart(2, '0'),
            value: h.toString(),
          }))}
        />
        <Select
          aria-label={`${label} minute`}
          size="compact"
          value={value.minute.toString()}
          onChange={(v) => v != null && onChange({ minute: Number(v) })}
          options={MINUTES_STEP_5.map((m) => ({
            label: m.toString().padStart(2, '0'),
            value: m.toString(),
          }))}
        />
        <Select
          aria-label={`${label} AM or PM`}
          size="compact"
          value={value.format}
          onChange={(v) =>
            (v === 'AM' || v === 'PM') && onChange({ format: v })
          }
          options={[
            { label: 'AM', value: 'AM' },
            { label: 'PM', value: 'PM' },
          ]}
        />
      </div>
    </div>
  );
}
